/**
 * The ONE delete-confirmation assembly (F8, integration review 2026-09-26) shared by the blob delete (also used by the
 * Data Lake view) and the file-share delete. UI/UX fix round A (review U15): structured instead of one run-on paragraph
 * — the title says what and how many, a "From" line where, then a list (the first ten items, folders marked with what
 * goes with them, the rest counted), a Recovery block with one line per fact, and "This cannot be undone." when nothing
 * the adapter can prove would bring the items back. Each service supplies its item description, its single-item title,
 * its recovery lines and whether the delete is irreversible. Whole-resource deletes (container, share, table, queue)
 * keep their typed-name dialogs.
 */
export type DeleteConfirmationItem = { label: string; folder: boolean; detail?: string };

export type DeleteConfirmationBody = {
  items: DeleteConfirmationItem[];
  /** Selected items beyond the listed ones. */
  more: number;
  /** Where the items are ("images/2026/"); absent when they have no one common place. */
  location?: string;
  recovery: string[];
  irreversible: boolean;
};

export type StorageDeleteConfirmation = {
  title: string;
  /** The body as plain text (the dialog renders `body`; typed-name deletes have no body). */
  description: string;
  confirmLabel: string;
  requireTypedName?: { value: string; label: string };
  body?: DeleteConfirmationBody;
};

/** At most this many selected items are listed in the confirmation; the rest are counted. */
export const MAX_NAMED_DELETE_ITEMS = 10;
export const IRREVERSIBLE_LINE = 'This cannot be undone.';

export function deleteConfirmationCopy<T>(args: {
  items: readonly T[];
  describe: (item: T) => DeleteConfirmationItem;
  singleTitle: (item: T) => string;
  location?: string;
  recovery: string[];
  irreversible: boolean;
}): StorageDeleteConfirmation {
  const count = args.items.length;
  const items = args.items.slice(0, MAX_NAMED_DELETE_ITEMS).map(args.describe);
  const body: DeleteConfirmationBody = { items, more: Math.max(0, count - MAX_NAMED_DELETE_ITEMS), recovery: args.recovery, irreversible: args.irreversible, ...(args.location ? { location: args.location } : {}) };
  return { title: count === 1 ? args.singleTitle(args.items[0]) : `Delete ${count} items?`, description: deleteBodyText(body), confirmLabel: 'Delete', body };
}

/** The body as lines of text, in the order the dialog shows them. */
export function deleteBodyText(body: DeleteConfirmationBody): string {
  const lines = [
    ...(body.location ? [`From ${body.location}`] : []),
    ...body.items.map((item) => `${item.label}${item.detail ? ` — ${item.detail}` : ''}`),
    ...(body.more > 0 ? [`and ${body.more} more`] : []),
    'Recovery',
    ...body.recovery,
    ...(body.irreversible ? [IRREVERSIBLE_LINE] : []),
  ];
  return lines.join('\n');
}

/** The common parent of paths ("2026/09/a.txt", "2026/09/b/") as "2026/09/", or '' when they share none. */
export function commonParent(paths: readonly string[]): string {
  const parents = paths.map((path) => path.replace(/\/+$/u, '').split('/').slice(0, -1));
  if (parents.length === 0) return '';
  const shared: string[] = [];
  for (let index = 0; index < parents[0].length; index += 1) {
    const segment = parents[0][index];
    if (!parents.every((parts) => parts[index] === segment)) break;
    shared.push(segment);
  }
  return shared.length > 0 ? `${shared.join('/')}/` : '';
}
